import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Plus, Zap, History as HistoryIcon, TrendingUp } from "lucide-react";

type Profile = { plan: string; credits_used: number; credits_reset_at: string };
type Recent = { id: string; product_name: string; seo_title: string; seo_score: number; created_at: string };

export default function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [recent, setRecent] = useState<Recent[]>([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const [{ data: p }, { data: r }, { count }] = await Promise.all([
        supabase.from("profiles").select("plan, credits_used, credits_reset_at").eq("id", user.id).maybeSingle(),
        supabase.from("seo_results").select("id, product_name, seo_title, seo_score, created_at").order("created_at", { ascending: false }).limit(5),
        supabase.from("seo_results").select("*", { count: "exact", head: true }),
      ]);
      setProfile(p);
      setRecent(r ?? []);
      setTotal(count ?? 0);
    })();
  }, [user]);

  const planKey = (profile?.plan ?? "free") as "free" | "plus" | "pro";
  const PLAN_INFO = {
    free: { limit: 5, window: "today", paid: false },
    plus: { limit: 120, window: "this month", paid: true },
    pro: { limit: 300, window: "this month", paid: true },
  } as const;
  const planInfo = PLAN_INFO[planKey] ?? PLAN_INFO.free;
  const isPaid = planInfo.paid;
  const limit = planInfo.limit;
  const used = profile?.credits_used ?? 0;
  const remaining = Math.max(limit - used, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground">Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}.</p>
        </div>
        <Button asChild className="bg-gradient-primary shadow-glow">
          <Link to="/generate"><Plus className="h-4 w-4 mr-2" />Create New SEO</Link>
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="bg-gradient-card border-border/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Plan</CardDescription>
              <Zap className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="capitalize text-2xl flex items-center gap-2">
              {profile?.plan ?? "-"}
              {isPaid && <Badge className="bg-gradient-primary border-0">{planKey.toUpperCase()}</Badge>}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {planKey !== "pro" && (
              <Button asChild variant="outline" size="sm" className="w-full"><Link to="/settings">Upgrade plan</Link></Button>
            )}
          </CardContent>
        </Card>

        <Card className="bg-gradient-card border-border/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Credits used</CardDescription>
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-2xl">{used} <span className="text-base font-normal text-muted-foreground">/ {limit}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <Progress value={(used / limit) * 100} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">{remaining} remaining {planInfo.window}</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-card border-border/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardDescription>Total generations</CardDescription>
              <TrendingUp className="h-4 w-4 text-primary" />
            </div>
            <CardTitle className="text-2xl">{total}</CardTitle>
          </CardHeader>
          <CardContent>
            <Button asChild variant="ghost" size="sm" className="w-full"><Link to="/history"><HistoryIcon className="h-4 w-4 mr-2" />View history</Link></Button>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-gradient-card border-border/50">
        <CardHeader>
          <CardTitle>Recent SEO results</CardTitle>
          <CardDescription>Your latest 5 generations</CardDescription>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Sparkles className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="font-medium">No generations yet</p>
              <p className="text-sm mb-4">Create your first AI-powered SEO listing.</p>
              <Button asChild className="bg-gradient-primary"><Link to="/generate">Get started</Link></Button>
            </div>
          ) : (
            <ul className="divide-y">
              {recent.map((r) => (
                <li key={r.id} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.product_name}</p>
                    <p className="text-sm text-muted-foreground truncate">{r.seo_title}</p>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Badge variant="secondary">{r.seo_score}/100</Badge>
                    <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
